import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bug, Mail, Flag, RefreshCw, ExternalLink, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Option A (Feb 2026) — lightweight read-only view of public form
 * submissions that bypass the main support_tickets inbox: beta feedback,
 * contact inquiries, moderation reports. Renders below <SupportInbox/>
 * on the /ops Support tab so operators can see everything in one place
 * without a schema migration. Option C (single-source-of-truth) is
 * queued in ROADMAP.md.
 */
const SOURCE_META = {
  beta_feedback: {
    label: 'Beta Feedback',
    icon: Bug,
    badgeClass: 'bg-purple-500/20 text-purple-300 border-purple-400/40',
  },
  contact: {
    label: 'Contact',
    icon: Mail,
    badgeClass: 'bg-blue-500/20 text-blue-300 border-blue-400/40',
  },
  report: {
    label: 'Report',
    icon: Flag,
    badgeClass: 'bg-red-500/20 text-red-300 border-red-400/40',
  },
};

const SOURCE_FILTERS = ['all', 'beta_feedback', 'contact', 'report'];

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export default function PublicSubmissionsPanel({ darkMode = false }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ beta_feedback: 0, contact: 0, report: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [approving, setApproving] = useState(null); // agency_id being approved

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/support/tickets/public-submissions`, {
        withCredentials: true,
      });
      setItems(res.data.items || []);
      setCounts(res.data.counts || { beta_feedback: 0, contact: 0, report: 0, total: 0 });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const approveNgo = async (item) => {
    if (!item.linked_agency_id) return;
    setApproving(item.linked_agency_id);
    try {
      const res = await axios.post(
        `${API}/api/adoption-agencies/${item.linked_agency_id}/approve`,
        {},
        { withCredentials: true }
      );
      const { promo_url, email_sent, message } = res.data;
      toast.success(message, {
        description: (
          <>
            Live at{' '}
            <a href={promo_url} target="_blank" rel="noopener noreferrer" className="underline">
              {promo_url}
            </a>
            {email_sent && ' · Welcome email sent'}
          </>
        ),
        duration: 8000,
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving(null);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const visible = filter === 'all' ? items : items.filter((i) => i.source === filter);

  const cardBg = darkMode
    ? 'bg-slate-800 border-slate-700 text-slate-100'
    : 'bg-white border-gray-200';
  const rowHover = darkMode
    ? 'hover:bg-slate-700/50'
    : 'hover:bg-gray-50';
  const mutedText = darkMode ? 'text-slate-400' : 'text-gray-500';

  return (
    <Card className={cardBg} data-testid="public-submissions-panel">
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            Recent public submissions
            <Badge variant="outline" data-testid="public-submissions-total">
              {counts.total}
            </Badge>
          </CardTitle>
          <p className={`text-xs mt-1 ${mutedText}`}>
            Read-only view of beta feedback, contact form, and moderation reports
            that don&apos;t flow through the main support inbox.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="public-submissions-refresh"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
        </Button>
      </CardHeader>
      <CardContent>
        {/* Filter chips */}
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="public-submissions-filters">
          {SOURCE_FILTERS.map((f) => {
            const isActive = filter === f;
            const label = f === 'all' ? `All (${counts.total})` : `${SOURCE_META[f]?.label} (${counts[f] || 0})`;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`public-submissions-filter-${f}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  isActive
                    ? 'bg-purple-600 border-purple-500 text-white'
                    : darkMode
                    ? 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="text-sm text-red-400 mb-3" data-testid="public-submissions-error">
            {error}
          </div>
        )}

        {loading && !items.length ? (
          <div className={`text-sm ${mutedText} py-8 text-center`}>
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Loading submissions…
          </div>
        ) : visible.length === 0 ? (
          <div className={`text-sm ${mutedText} py-8 text-center`} data-testid="public-submissions-empty">
            No submissions in this category yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-700/50" data-testid="public-submissions-list">
            {visible.map((item) => {
              const meta = SOURCE_META[item.source] || SOURCE_META.beta_feedback;
              const Icon = meta.icon;
              return (
                <li
                  key={`${item.source}-${item.id}`}
                  className={`py-3 px-2 rounded transition-colors ${rowHover}`}
                  data-testid={`public-submission-${item.source}-${item.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${meta.badgeClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-xs ${meta.badgeClass}`}>
                          {meta.label}
                        </Badge>
                        {item.type && (
                          <span className={`text-xs ${mutedText}`}>{item.type}</span>
                        )}
                        <span className={`text-xs ${mutedText}`}>·</span>
                        <span className={`text-xs ${mutedText}`}>{fmtTime(item.created_at)}</span>
                        {item.status && item.status !== 'new' && (
                          <Badge variant="outline" className="text-xs">
                            {item.status}
                          </Badge>
                        )}
                      </div>
                      <div className="font-medium text-sm mt-1 truncate">
                        {item.title}
                      </div>
                      {item.snippet && (
                        <div className={`text-xs mt-1 ${mutedText} line-clamp-2`}>
                          {item.snippet}
                        </div>
                      )}
                      <div className={`text-xs mt-1 ${mutedText}`}>
                        from <span className="font-medium">{item.submitter}</span>
                      </div>
                    </div>
                    {item.link && (
                      <a
                        href={item.link}
                        className={`shrink-0 text-xs ${mutedText} hover:text-white flex items-center gap-1`}
                        data-testid={`public-submission-open-${item.id}`}
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  {/* NGO signup: inline Approve button */}
                  {item.source === 'contact' && item.type === 'ngo_signup' && item.linked_agency_id && item.status !== 'resolved' && (
                    <div className="mt-2 flex items-center gap-2 pl-11">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs bg-green-500/10 border-green-400/40 text-green-300 hover:bg-green-500/20"
                        onClick={() => approveNgo(item)}
                        disabled={approving === item.linked_agency_id}
                        data-testid={`ngo-approve-btn-${item.linked_agency_id}`}
                      >
                        {approving === item.linked_agency_id ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Approving…</>
                        ) : (
                          <><CheckCircle2 className="w-3 h-3 mr-1" /> Approve NGO</>
                        )}
                      </Button>
                      {item.linked_agency_slug && (
                        <a
                          href={`/ngo/${item.linked_agency_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs ${mutedText} hover:text-white flex items-center gap-1`}
                          data-testid={`ngo-preview-link-${item.linked_agency_id}`}
                        >
                          Preview page <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
